import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { HttpClient } from '@angular/common/http'
import { DialogModule } from 'primeng/dialog'
import { ButtonModule } from 'primeng/button'
import { InputTextModule } from 'primeng/inputtext'
import { MultiSelectModule } from 'primeng/multiselect'
import { DatePickerModule } from 'primeng/datepicker'
import { DropdownModule } from 'primeng/dropdown'
import { TooltipModule } from 'primeng/tooltip'
import { finalize } from 'rxjs'
import {
  InterventionsService,
  CreatePoolAffairResponse,
} from '../../../../../interventions/services/interventions.service'
import { ProductsService } from '../../../../../products/services/products.service'
import { MarketTypesService } from '../../../../../markets-types/services/market-types.service'
import { ProductModel } from '../../../../../../core/models/product.model'
import { MarketTypeModel } from '../../../../../../core/models/market-type.model'

/** Résultat de l'autocomplete adresse (API Adresse data.gouv.fr, BAN). */
interface AddressSearchResult {
  label: string
  address: string
  postalCode: string
  locality: string
  latitude: number
  longitude: number
}

interface SelectedProduct {
  product: ProductModel
  quantity: number
}

/** Réponse de création enrichie pour le parent (nom pour les notifications). */
export type PoolAffairCreatedEvent = CreatePoolAffairResponse & {
  customerLastname: string
}

/**
 * Création manuelle d'une affaire vivier (draft) depuis le Programmator.
 * Pensé pour la prise d'appel : 3 champs (nom, adresse BAN, produits),
 * fenêtre souhaitée optionnelle repliée par défaut.
 */
@Component({
  selector: 'app-pool-affair-create-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    DatePickerModule,
    DropdownModule,
    TooltipModule,
  ],
  templateUrl: './pool-affair-create-dialog.component.html',
  styleUrl: './pool-affair-create-dialog.component.scss',
})
export class PoolAffairCreateDialogComponent implements OnDestroy {
  private http = inject(HttpClient)
  private interventionsService = inject(InterventionsService)
  private productsService = inject(ProductsService)
  private marketTypesService = inject(MarketTypesService)

  /** Id du marché « Maison individuelle » (défaut de création). */
  private static readonly INDIVIDUAL_HOUSE_MARKET_ID = 1

  @Input() set visible(value: boolean) {
    if (value && !this._visible) this.onOpen()
    this._visible = value
  }
  get visible(): boolean {
    return this._visible
  }
  private _visible = false

  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() created = new EventEmitter<PoolAffairCreatedEvent>()

  // ── Formulaire ──
  customerLastname = ''
  addressQuery = ''
  selectedAddress: AddressSearchResult | null = null
  selectedProducts: ProductModel[] = []
  productQuantities = new Map<number, number>()
  /** N° de semaine ISO souhaitée : remplit la fenêtre lundi → dimanche. */
  wishedWeek: number | null = null
  wishedWeekHint = ''
  wishedWeekError = ''
  wishedPlanningStartDate: Date | null = null
  wishedPlanningEndDate: Date | null = null
  planningDeadline: Date | null = null

  // ── Catalogue produits (commun) ──
  products: ProductModel[] = []
  productsLoading = false

  // ── Marchés ──
  marketTypes: MarketTypeModel[] = []
  selectedMarketTypeId: number =
    PoolAffairCreateDialogComponent.INDIVIDUAL_HOUSE_MARKET_ID

  // ── Autocomplete BAN ──
  addressResults: AddressSearchResult[] = []
  addressSearchOpen = false
  addressSearchLoading = false
  addressActiveIndex = -1
  private addressSearchTimer: ReturnType<typeof setTimeout> | null = null
  private addressSearchSeq = 0

  submitting = false
  submitError: string | null = null

  ngOnDestroy(): void {
    if (this.addressSearchTimer) clearTimeout(this.addressSearchTimer)
  }

  private onOpen(): void {
    this.resetForm()
    if (this.products.length === 0 && !this.productsLoading) {
      this.productsLoading = true
      this.productsService
        .getProducts()
        .pipe(finalize(() => (this.productsLoading = false)))
        .subscribe({
          next: (products) => {
            this.products = (products ?? []).filter((p) => p.isActive)
          },
          error: () => {
            this.submitError = 'Impossible de charger le catalogue produits.'
          },
        })
    }
    if (this.marketTypes.length === 0) {
      this.marketTypesService.getMarketsTypes().subscribe({
        next: (marketTypes) => {
          this.marketTypes = marketTypes ?? []
          this.applyDefaultMarketType()
        },
        error: () => {
          // Non bloquant : le back applique « Maison individuelle » par défaut
        },
      })
    }
  }

  /** Défaut « Maison individuelle » (par nom, repli sur l'id 1 / le premier). */
  private applyDefaultMarketType(): void {
    const byName = this.marketTypes.find((m) =>
      m.name?.toLowerCase().includes('maison individuelle')
    )
    const byId = this.marketTypes.find(
      (m) => m.id === PoolAffairCreateDialogComponent.INDIVIDUAL_HOUSE_MARKET_ID
    )
    this.selectedMarketTypeId =
      byName?.id ?? byId?.id ?? this.marketTypes[0]?.id ?? this.selectedMarketTypeId
  }

  private resetForm(): void {
    this.customerLastname = ''
    this.addressQuery = ''
    this.selectedAddress = null
    this.selectedProducts = []
    this.productQuantities.clear()
    this.applyDefaultMarketType()
    this.wishedWeek = null
    this.wishedWeekHint = ''
    this.wishedWeekError = ''
    this.wishedPlanningStartDate = null
    this.wishedPlanningEndDate = null
    this.planningDeadline = null
    this.addressResults = []
    this.addressSearchOpen = false
    this.submitting = false
    this.submitError = null
  }

  close(): void {
    this._visible = false
    this.visibleChange.emit(false)
  }

  // ── Adresse (BAN) ──

  onAddressQueryChange(query: string): void {
    this.addressQuery = query
    this.selectedAddress = null
    if (this.addressSearchTimer) clearTimeout(this.addressSearchTimer)

    const q = query.trim()
    if (q.length < 4) {
      this.addressResults = []
      this.addressSearchOpen = false
      return
    }
    this.addressSearchTimer = setTimeout(() => this.fetchAddresses(q), 250)
  }

  private fetchAddresses(q: string): void {
    const seq = ++this.addressSearchSeq
    this.addressSearchLoading = true

    // Géoplateforme (data.geopf.fr) : successeur de api-adresse.data.gouv.fr
    // (BAN historique décommissionnée — renvoie des réponses vides).
    const url =
      'https://data.geopf.fr/geocodage/search' +
      `?q=${encodeURIComponent(q)}&autocomplete=1&limit=6`

    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (seq !== this.addressSearchSeq) return
        this.addressSearchLoading = false
        this.addressResults = ((res?.features ?? []) as any[])
          .map((f): AddressSearchResult | null => {
            const props = f?.properties ?? {}
            const coords = f?.geometry?.coordinates
            if (!Array.isArray(coords) || coords.length < 2) return null
            if (!props.label || !props.postcode || !props.city) return null
            return {
              label: props.label,
              address: props.name ?? props.street ?? props.label,
              postalCode: props.postcode,
              locality: props.city,
              latitude: coords[1],
              longitude: coords[0],
            }
          })
          .filter((r): r is AddressSearchResult => r !== null)
        this.addressSearchOpen = this.addressResults.length > 0
        this.addressActiveIndex = this.addressResults.length > 0 ? 0 : -1
      },
      error: () => {
        if (seq !== this.addressSearchSeq) return
        this.addressSearchLoading = false
        this.addressResults = []
        this.addressSearchOpen = false
      },
    })
  }

  selectAddress(result: AddressSearchResult): void {
    this.selectedAddress = result
    this.addressQuery = result.label
    this.addressResults = []
    this.addressSearchOpen = false
  }

  onAddressKeydown(event: KeyboardEvent): void {
    if (!this.addressSearchOpen || this.addressResults.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.addressActiveIndex =
        (this.addressActiveIndex + 1) % this.addressResults.length
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.addressActiveIndex =
        (this.addressActiveIndex - 1 + this.addressResults.length) %
        this.addressResults.length
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const result = this.addressResults[this.addressActiveIndex]
      if (result) this.selectAddress(result)
    } else if (event.key === 'Escape') {
      this.addressSearchOpen = false
    }
  }

  // ── Semaine souhaitée ──

  /**
   * Saisie d'un n° de semaine ISO : remplit la fenêtre souhaitée du lundi au
   * dimanche de cette semaine. Année en cours, ou suivante si la semaine est
   * déjà entièrement passée. Les dates restent ajustables manuellement.
   */
  onWishedWeekChange(value: number | string | null): void {
    this.wishedWeekError = ''
    const week = Number(value)

    if (!value || isNaN(week)) {
      this.wishedWeek = null
      this.wishedWeekHint = ''
      return
    }

    if (week < 1 || week > 53 || !Number.isInteger(week)) {
      this.wishedWeek = week
      this.wishedWeekHint = ''
      this.wishedWeekError = 'N° de semaine invalide (1 à 53).'
      return
    }

    this.wishedWeek = week

    const now = new Date()
    let year = now.getFullYear()
    let [monday, sunday] = this.isoWeekRange(week, year)

    // Semaine entièrement passée → année suivante
    if (sunday.getTime() < now.setHours(0, 0, 0, 0)) {
      year += 1
      ;[monday, sunday] = this.isoWeekRange(week, year)
    }

    this.wishedPlanningStartDate = monday
    this.wishedPlanningEndDate = sunday

    const fmt = (d: Date) =>
      d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    this.wishedWeekHint = `S${week} ${year} : du ${fmt(monday)} au ${fmt(sunday)}`
  }

  /** Modification manuelle des dates : la semaine saisie ne fait plus foi. */
  onWishedDatesEdited(): void {
    this.wishedWeek = null
    this.wishedWeekHint = ''
    this.wishedWeekError = ''
  }

  /** [lundi, dimanche] de la semaine ISO `week` de l'année `year`. */
  private isoWeekRange(week: number, year: number): [Date, Date] {
    // Le 4 janvier est toujours en semaine ISO 1
    const jan4 = new Date(year, 0, 4)
    const jan4IsoDay = jan4.getDay() === 0 ? 7 : jan4.getDay()
    const week1Monday = new Date(year, 0, 4 - (jan4IsoDay - 1))

    const monday = new Date(week1Monday)
    monday.setDate(week1Monday.getDate() + (week - 1) * 7)
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)

    return [monday, sunday]
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  // ── Produits ──

  onProductsChange(products: ProductModel[]): void {
    this.selectedProducts = products ?? []
    for (const product of this.selectedProducts) {
      if (!this.productQuantities.has(product.id)) {
        this.productQuantities.set(product.id, 1)
      }
    }
  }

  getQuantity(product: ProductModel): number {
    return this.productQuantities.get(product.id) ?? 1
  }

  changeQuantity(product: ProductModel, delta: number): void {
    const next = Math.max(1, this.getQuantity(product) + delta)
    this.productQuantities.set(product.id, next)
  }

  /** Durée de service totale estimée, en minutes. */
  get totalDurationMin(): number {
    return this.selectedProducts.reduce(
      (sum, p) => sum + (p.duration || 0) * this.getQuantity(p),
      0
    )
  }

  get totalDurationLabel(): string {
    const min = this.totalDurationMin
    if (min <= 0) return ''
    const h = Math.floor(min / 60)
    const m = min % 60
    return h > 0 ? `${h}h${m > 0 ? String(m).padStart(2, '0') : ''}` : `${m} min`
  }

  // ── Soumission ──

  get canSubmit(): boolean {
    return (
      !this.submitting &&
      this.customerLastname.trim().length > 0 &&
      this.selectedAddress !== null &&
      this.selectedProducts.length > 0
    )
  }

  submit(): void {
    if (!this.canSubmit || !this.selectedAddress) return

    this.submitting = true
    this.submitError = null

    this.interventionsService
      .createPoolAffair({
        customerLastname: this.customerLastname.trim(),
        address: this.selectedAddress.address,
        postalCode: this.selectedAddress.postalCode,
        locality: this.selectedAddress.locality,
        latitude: this.selectedAddress.latitude,
        longitude: this.selectedAddress.longitude,
        affairProducts: this.selectedProducts.map((p) => ({
          productId: p.id,
          quantity: this.getQuantity(p),
        })),
        marketTypeId: this.selectedMarketTypeId,
        wishedPlanningStartDate: this.wishedPlanningStartDate
          ? this.toIsoDate(this.wishedPlanningStartDate)
          : null,
        wishedPlanningEndDate: this.wishedPlanningEndDate
          ? this.toIsoDate(this.wishedPlanningEndDate)
          : null,
        planningDeadline: this.planningDeadline
          ? this.toIsoDate(this.planningDeadline)
          : null,
      })
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (response) => {
          this.created.emit({
            ...response,
            customerLastname: this.customerLastname.trim(),
          })
          this.close()
        },
        error: (err) => {
          this.submitError =
            err?.error?.message ??
            "Une erreur est survenue lors de la création de l'affaire."
        },
      })
  }
}
